import { createClient } from '@supabase/supabase-js';

// These will be replaced at build time via Vite's env handling
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const apiUrl = import.meta.env.VITE_API_URL || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    'Supabase credentials not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

/**
 * Sync refresh token to HttpOnly cookie whenever SDK refreshes the token.
 * This ensures WebChat (subdomain) always has the latest refresh_token.
 *
 * Without this, the SDK would refresh the token in memory but the cookie
 * would stay stale, causing "refresh_token_already_used" errors in WebChat.
 */
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'TOKEN_REFRESHED' && session?.refresh_token) {
    syncRefreshTokenCookie(session.refresh_token, session.access_token);
  }
});

async function syncRefreshTokenCookie(refreshToken: string, accessToken: string): Promise<void> {
  if (!apiUrl) {
    console.warn('[Auth] VITE_API_URL not configured, skipping refresh token sync');
    return;
  }

  try {
    const response = await fetch(`${apiUrl}/v1/auth/set-refresh-cookie`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      console.error('[Auth] Failed to sync refresh token cookie:', response.status);
    } else {
      console.log('[Auth] Refresh token cookie synced successfully');
    }
  } catch (err) {
    console.error('[Auth] Error syncing refresh token cookie:', err);
  }
}

// Helper to get current session token for API calls
export async function getAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token || null;
}
